import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, ArrowRight, ShieldCheck, MapPin, Scale, AlertTriangle, Award, Search } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import RelatedCaseResults from '@/components/RelatedCaseResults';
import LegalConceptsCallout from '@/components/LegalConceptsCallout';

const CaseResultRestaurantSlipFallPage = () => {
  const caseFacts = [
    { icon: Scale, label: 'Case type', value: 'Premises liability' },
    { icon: Award, label: 'Result', value: 'Six-figure pre-litigation settlement' },
    { icon: AlertTriangle, label: 'Injury', value: 'Dominant-wrist fracture requiring permanent hardware' },
    { icon: MapPin, label: 'Location', value: 'Western Washington' },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="case-result-restaurant-slip-fall">
      <SEOHead
        noindex={true}
        title="Six-Figure Settlement for Wet-Floor Slip and Wrist Fracture | Amy C. Brown Law"
        description="Amy C. Brown secured a six-figure settlement after a slip on a wet floor at a business caused a dominant-wrist fracture requiring permanent hardware."
        pageType="article"
        articleData={{
          title: 'Six-Figure Settlement After a Wet-Floor Fall Caused a Dominant-Wrist Fracture',
          description: 'Case study: slip and fall on a wet floor at a business in Western Washington caused a dominant-wrist fracture requiring permanent hardware. Six-figure pre-litigation settlement.',
          datePublished: '2026-08-26',
          dateModified: '2026-08-26',
          articleSection: 'Slip and Fall Case Results',
          keywords: 'slip and fall, premises liability, wet floor injury, wrist fracture settlement, Washington slip and fall attorney, dominant hand injury, permanent hardware, six-figure settlement',
          about: [
            { '@type': 'Thing', name: 'Premises liability', sameAs: 'https://en.wikipedia.org/wiki/Premises_liability' },
            { '@type': 'Thing', name: 'Slip and fall', sameAs: 'https://en.wikipedia.org/wiki/Slip_and_fall' },
            { '@type': 'Thing', name: 'Distal radius fracture', sameAs: 'https://en.wikipedia.org/wiki/Distal_radius_fracture' }
          ]
        }}
        faqData={[
          {
            question: 'Does every slip and fall at a business result in a settlement?',
            answer: 'No. A fall inside a business does not automatically establish negligence. Liability depends on the specific facts — what created the hazard, whether the business knew or should have known about it, and what precautions were taken (mats, warning signs, cleaning schedules).',
          },
          {
            question: 'What evidence matters most in a wet-floor slip and fall claim?',
            answer: 'Photographs of the hazard, surveillance-camera footage, witness statements, weather conditions, incident reports, cleaning and inspection records, presence or absence of floor mats and warning signs, evidence of how long the hazard existed, shoes worn, and medical documentation and lost-income proof.',
          },
          {
            question: 'How is compensation determined for a wrist fracture?',
            answer: 'A diagnosis alone does not determine value. The full impact matters — inability to continue in your profession, lost income, pain and disruption, effect on family life, and whether surgery required permanent hardware.',
          },
        ]}
      />

      {/* Hero — abstract wet-floor / caution motif */}
      <section className="pt-16 pb-14 md:pt-20 md:pb-20 bg-white border-b border-slate-100">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <nav className="mb-6" aria-label="Breadcrumb">
            <Link to="/case-results" className="text-sm text-teal-700 hover:underline inline-flex items-center gap-1">
              <ArrowRight className="w-4 h-4 rotate-180" /> All Case Results
            </Link>
          </nav>

          <div className="grid lg:grid-cols-[1fr_320px] gap-10 lg:gap-16 items-start">
            <div>
              <span className="inline-block bg-teal-50 text-teal-700 px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase mb-5" data-testid="case-badge">
                Case Result · Slip and Fall
              </span>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-secondary leading-tight mb-5">
                Six-Figure Settlement for Dominant-Wrist Fracture from Wet-Floor Slip and Fall
              </h1>
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl">
                A slip on a wet floor at a business caused a dominant-wrist fracture requiring permanent hardware. Amy secured a six-figure settlement without filing a lawsuit.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="tel:425-675-1224"
                  data-testid="case-hero-call"
                  className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-full font-semibold hover:bg-primary/90 transition"
                >
                  <Phone className="w-4 h-4" /> Free Consultation
                </a>
                <a
                  href="sms:425-675-1224"
                  className="inline-flex items-center gap-2 border-2 border-slate-300 text-secondary px-6 py-3 rounded-full font-semibold hover:border-teal-600 hover:text-teal-700 transition"
                >
                  <MessageCircle className="w-4 h-4" /> Text Me
                </a>
              </div>
            </div>

            {/* Abstract graphic — rain drops + caution triangle, brand palette */}
            <div className="hidden lg:block" aria-hidden="true">
              <svg viewBox="0 0 320 320" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
                <defs>
                  <linearGradient id="rf-bg" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#EAF3F3" />
                    <stop offset="100%" stopColor="#F5EEEB" />
                  </linearGradient>
                </defs>
                <rect x="0" y="0" width="320" height="320" rx="24" fill="url(#rf-bg)" />
                {/* Rain streaks */}
                <g stroke="#4A9B9B" strokeWidth="2" strokeLinecap="round" opacity="0.35">
                  <line x1="60" y1="40" x2="52" y2="70" />
                  <line x1="90" y1="30" x2="82" y2="60" />
                  <line x1="120" y1="45" x2="112" y2="75" />
                  <line x1="200" y1="35" x2="192" y2="65" />
                  <line x1="240" y1="50" x2="232" y2="80" />
                  <line x1="270" y1="40" x2="262" y2="70" />
                </g>
                {/* Caution triangle */}
                <g>
                  <polygon
                    points="160,105 240,235 80,235"
                    fill="#E8A0A8"
                    opacity="0.95"
                    stroke="#ffffff"
                    strokeWidth="4"
                    strokeLinejoin="round"
                  />
                  <rect x="155" y="150" width="10" height="45" rx="2" fill="#ffffff" />
                  <circle cx="160" cy="212" r="5" fill="#ffffff" />
                </g>
                {/* Water puddle base */}
                <ellipse cx="160" cy="260" rx="110" ry="10" fill="#4A9B9B" opacity="0.18" />
                <ellipse cx="120" cy="272" rx="40" ry="4" fill="#4A9B9B" opacity="0.22" />
                <ellipse cx="205" cy="278" rx="30" ry="3" fill="#4A9B9B" opacity="0.22" />
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Case Facts */}
      <section className="py-10 bg-slate-50" data-testid="case-facts">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {caseFacts.map(({ icon: Icon, label, value }) => (
              <div key={label} className="bg-white rounded-2xl p-5 border border-slate-100">
                <div className="w-10 h-10 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5" />
                </div>
                <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">{label}</p>
                <p className="text-secondary font-medium leading-snug">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full narrative */}
      <article className="py-16 md:py-20 bg-white" data-testid="case-narrative">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div className="prose prose-slate prose-lg max-w-none">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mt-0">What Happened</h2>
            <p className="lead text-lg text-slate-700 leading-relaxed">
              It had been raining throughout the day when the client entered a business in Western Washington. Water had been tracked across the hard floor, but there were no floor mats or warning signs near the wet area.
            </p>
            <p className="text-slate-700 leading-relaxed">
              The client slipped and fell, fracturing the dominant wrist. The injury made it impossible to continue in a profession that required constant use of both hands, and for an extended period the client was unable to lift or hold young children in the family.
            </p>

            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mt-12">The Business Initially Disputed the Fall</h2>
            <p className="text-slate-700 leading-relaxed">
              The business initially denied that the fall had happened on its property. Although there were multiple cameras on-site, the business did not provide the requested footage. It eventually stopped disputing that the fall occurred, but then attempted to blame the client for wearing ordinary athletic shoes after walking outside in the rain.
            </p>
            <p className="text-slate-700 leading-relaxed">
              That argument avoided the real question: What reasonable precautions had the business taken when rainwater was being tracked onto a slippery floor? There were no mats and no signs warning customers that the floor was wet.
            </p>
            <p className="text-slate-700 leading-relaxed">
              Not every slip and fall makes a business legally responsible. Liability depends on the specific facts — what created the hazard, whether the business knew or should have known about it, and what precautions were taken to protect customers.
            </p>

            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mt-12">Showing the Full Effect of the Injury</h2>
            <p className="text-slate-700 leading-relaxed">
              A wrist fracture may sound straightforward, but the value of an injury claim cannot be determined by the diagnosis alone. I focused on how the fracture affected the client's actual life — the inability to continue working, lost income during recovery and a career transition, significant pain and disruption, an inability to lift or hold young children in the family, and permanent hardware in the dominant wrist following surgery.
            </p>
            <p className="text-slate-700 leading-relaxed">
              The client eventually recovered and retrained for different work. However, working hard to rebuild did not erase the income lost, the pain experienced, or the time lost with family.
            </p>

            <blockquote className="not-prose my-10 border-l-4 border-teal-600 bg-teal-50/60 px-6 py-5 rounded-r-lg">
              <p className="text-lg md:text-xl text-secondary font-medium leading-snug italic">
                "A broken wrist may sound straightforward, but the value of an injury claim cannot be determined by the diagnosis alone."
              </p>
              <p className="text-sm text-slate-500 mt-3">— Amy C. Brown</p>
            </blockquote>

            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mt-12">The Result</h2>
            <p className="text-slate-700 leading-relaxed">
              I obtained a six-figure settlement without filing a lawsuit. The settlement paid outstanding medical expenses, compensated the client for lost income, and recognized the physical pain and emotional disruption the injury caused.
            </p>
            <p className="text-slate-700 leading-relaxed">
              Resolving the claim before litigation was especially important because the client needed financial relief without a lengthy lawsuit, deposition, or formal discovery.
            </p>
            <p className="text-slate-700 leading-relaxed">
              Without legal representation, the client would have faced the difficult task of locating the business's insurer and risk-management representatives alone. Even if that had succeeded in getting medical expenses paid, the compensation likely would not have reflected the full lost income, pain, and broader effect the injury had on the client's life.
            </p>

            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mt-12">What Other Injured Customers Should Understand</h2>
            <p className="text-slate-700 leading-relaxed">
              A fall inside a business does not automatically establish negligence. The surrounding facts matter.
            </p>
            <p className="text-slate-700 leading-relaxed">Important evidence may include:</p>
            <ul className="text-slate-700 leading-relaxed space-y-2">
              <li>Photographs or video of the dangerous condition</li>
              <li>Surveillance-camera footage</li>
              <li>Witness statements</li>
              <li>Weather conditions</li>
              <li>Incident reports</li>
              <li>Cleaning and inspection records</li>
              <li>The presence or absence of floor mats and warning signs</li>
              <li>Evidence showing how long the hazardous condition existed</li>
              <li>The shoes and clothing worn at the time</li>
              <li>Medical documentation and proof of lost income</li>
            </ul>
            <p className="text-slate-700 leading-relaxed">
              Businesses and insurers may argue that the hazard was obvious, that the injured person was careless, or even that the incident did not happen as described. Preserving evidence and identifying the correct insurance representatives early can make a meaningful difference.
            </p>
            <p className="text-slate-700 leading-relaxed">
              If you were injured after slipping on a wet floor at a business, I can evaluate what happened and explain your options.
            </p>
          </div>
        </div>
      </article>

      {/* Legal Concepts callout — educational moment that reinforces expertise
          and internally links to the glossary. */}
      <LegalConceptsCallout
        concepts={[
          {
            term: 'Premises liability',
            slug: 'premises-liability',
            explanation:
              "A property owner's legal duty to keep visitors reasonably safe from known or discoverable hazards. When rain is being tracked onto a slippery tile floor throughout the day, basic precautions like floor mats and warning signs are usually part of that duty.",
          },
          {
            term: 'Constructive notice',
            slug: 'constructive-notice',
            explanation:
              'A business is treated as knowing about a hazard if the hazard existed long enough that they should have discovered it through reasonable care. Continuous tracked-in rainwater over the course of a day is exactly the kind of condition where constructive notice comes into play.',
          },
          {
            term: 'Comparative negligence',
            slug: 'comparative-negligence',
            explanation:
              "Washington follows pure comparative negligence (RCW 4.22). Even if a defendant argues the injured person shared some fault — for example, by wearing ordinary shoes in the rain — the injured person can still recover damages, reduced by their percentage of fault.",
          },
        ]}
      />

      {/* CTA */}
      <section className="py-16 md:py-20 bg-teal-600 text-white" data-testid="case-cta">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <ShieldCheck className="w-12 h-12 mx-auto mb-5 text-teal-100" aria-hidden="true" />
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
            Injured in a Slip and Fall?
          </h2>
          <p className="text-lg md:text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            Contact Amy C. Brown Law for a free consultation. You pay no attorney fee unless I recover compensation for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:425-675-1224"
              data-testid="case-cta-call"
              className="inline-flex items-center justify-center gap-2 bg-white text-teal-700 px-8 py-4 rounded-full font-bold hover:bg-teal-50 transition shadow-lg"
            >
              <Phone className="w-5 h-5" /> Call (425) 675-1224
            </a>
            <a
              href="sms:425-675-1224"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full font-bold hover:bg-white/10 transition"
            >
              <MessageCircle className="w-5 h-5" /> Text Me
            </a>
          </div>
        </div>
      </section>

      {/* Related Resources */}
      <section className="py-14 md:py-16 bg-white" data-testid="case-related">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <h3 className="font-heading text-2xl font-bold text-secondary mb-6 text-center">Related Resources</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/slip-and-falls" className="group bg-slate-50 hover:bg-teal-50 border border-slate-100 rounded-2xl p-5 transition">
              <p className="text-xs uppercase tracking-widest text-teal-700 font-semibold mb-2">Practice area</p>
              <p className="font-semibold text-secondary group-hover:text-teal-700 flex items-center gap-2">
                Slip and Fall Injuries <ArrowRight className="w-4 h-4" />
              </p>
            </Link>
            <Link to="/compensation-estimator" className="group bg-slate-50 hover:bg-teal-50 border border-slate-100 rounded-2xl p-5 transition">
              <p className="text-xs uppercase tracking-widest text-teal-700 font-semibold mb-2">Tool</p>
              <p className="font-semibold text-secondary group-hover:text-teal-700 flex items-center gap-2">
                What Is My Case Worth? Free Case Calculator <ArrowRight className="w-4 h-4" />
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-10 bg-slate-50 border-t border-slate-100" data-testid="case-disclaimer">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="flex items-start gap-3 text-sm text-slate-600 leading-relaxed">
            <Search className="w-4 h-4 text-slate-400 mt-1 flex-shrink-0" aria-hidden="true" />
            <p>
              <strong className="text-secondary">Disclaimer:</strong> Every case is different. This result does not guarantee or predict the outcome of another case. Certain details have been generalized or omitted to protect the client's identity. Past results do not constitute a guarantee, warranty, or prediction regarding the outcome of your legal matter.
            </p>
          </div>
        </div>
      </section>

      {/* Cross-links to other case results */}
      <RelatedCaseResults currentSlug="restaurant-slip-fall-wrist-fracture" />
    </div>
  );
};

export default CaseResultRestaurantSlipFallPage;
